import { Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/** Which representation of the plan the panel shows. */
export type PlanView = 'list' | 'timeline';

export const isPlanView = (value: unknown): value is PlanView =>
  value === 'list' || value === 'timeline';

export type PlanViewSwitcherProps = {
  value: PlanView;
  onChange: (next: PlanView) => void;
};

const OPTIONS = [
  { view: 'list', labelKey: 'term.List', icon: 'format_list_bulleted' },
  { view: 'timeline', labelKey: 'term.Timeline', icon: 'view_timeline' },
] as const satisfies readonly { view: PlanView; labelKey: string; icon: string }[];

/**
 * Segmented control for the plan's two views. A sunken pill track with the
 * selected option raised out of it — the same figure/ground trick the design
 * system uses for filter pills, which reads as "one of these" rather than "two
 * buttons".
 */
export const PlanViewSwitcher = ({ value, onChange }: PlanViewSwitcherProps) => {
  const { t } = useTranslation('pmCommon');
  return (
    <Box
      data-automation-id="plan-view-switcher"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '2px',
        p: '2px',
        ml: 0.5,
        borderRadius: 'var(--border-radius-pill)',
        bgcolor: 'var(--bg-sunken)',
      }}
    >
      {OPTIONS.map(({ view, labelKey, icon }) => {
        const selected = value === view;
        return (
          <Button
            key={view}
            size="small"
            variant="text"
            color="secondary"
            aria-pressed={selected}
            onClick={() => onChange(view)}
            startIcon={<MaterialSymbol name={icon} size={16} />}
            data-automation-id={`plan-view-${view}`}
            data-selected={selected}
            sx={
              // The selected option is the panel surface lifted out of the sunken
              // track, not a filled primary button: this picks a view, it is not
              // the page's call to action.
              selected
                ? {
                    bgcolor: 'var(--bg-panel)',
                    color: 'var(--fg-1)',
                    '&:hover': { bgcolor: 'var(--bg-panel)' },
                  }
                : { color: 'var(--fg-3)' }
            }
          >
            {t(labelKey)}
          </Button>
        );
      })}
    </Box>
  );
};
